import React, { useCallback, useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { TailSpin } from "react-loader-spinner";

import { LOADING_STATUSES as STATUS } from "constants/common";
import { formatDate } from "helpers/format-date";
import {
  loadPhoneCalls,
  loadPhoneCall,
  clearSelectedPhoneCall,
  getPhoneCallsData,
  getPhoneCallsStatus,
} from "redux/slices/supplier-phone-calls";
import { useDebounce } from "hooks/use-debounce";
import Pagination from "components/pagination";
import PhoneCallModal from "./phone-call-modal";

const InboundArrow = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M12 4L4 12M4 12H9M4 12V7"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const OutboundArrow = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M4 12L12 4M12 4H7M12 4V9"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const SupplierPhoneCalls = () => {
  const dispatch = useDispatch();
  const phoneCalls = useSelector(getPhoneCallsData);
  const status = useSelector(getPhoneCallsStatus);

  const [selectedCall, setSelectedCall] = useState(null);
  const [page, setPage] = useState(1);
  const [fromNumber, setFromNumber] = useState("");
  const [toNumber, setToNumber] = useState("");
  const [debouncedFromNumber, setDebouncedFromNumber] = useState("");
  const [debouncedToNumber, setDebouncedToNumber] = useState("");

  const fetchPhoneCalls = useCallback(
    (currentPage, from, to) => {
      dispatch(
        loadPhoneCalls({ page: currentPage, fromNumber: from, toNumber: to })
      );
    },
    [dispatch]
  );

  useDebounce(
    () => {
      if (
        fromNumber !== debouncedFromNumber ||
        toNumber !== debouncedToNumber
      ) {
        setDebouncedFromNumber(fromNumber);
        setDebouncedToNumber(toNumber);
        setPage(1);
        fetchPhoneCalls(1, fromNumber, toNumber);
      }
    },
    [fromNumber, toNumber],
    700
  );

  useEffect(() => {
    fetchPhoneCalls(page, debouncedFromNumber, debouncedToNumber);
  }, [page]); // eslint-disable-line react-hooks/exhaustive-deps

  const onPageChange = useCallback((newPage) => {
    setPage(newPage);
  }, []);

  const onCallClick = useCallback(
    (call) => {
      setSelectedCall(call);
      dispatch(loadPhoneCall(call.id));
    },
    [dispatch]
  );

  const onModalClose = useCallback(() => {
    setSelectedCall(null);
    dispatch(clearSelectedPhoneCall());
  }, [dispatch]);

  return (
    <div className="yourTicketsSection" style={{ overflowY: "auto" }}>
      <div className="rowSpaceBetween">
        <h2 className="sectionHeader">Phone calls</h2>
        <div
          className="tickets-search-container"
          style={{ display: "flex", gap: "8px" }}
        >
          <input
            className="tickets-search-input"
            name="from_number"
            onChange={({ target }) => setFromNumber(target.value)}
            value={fromNumber}
            autoComplete="off"
            placeholder="From number"
          />
          <input
            className="tickets-search-input"
            name="to_number"
            onChange={({ target }) => setToNumber(target.value)}
            value={toNumber}
            autoComplete="off"
            placeholder="To number"
          />
        </div>
      </div>
      <div className="tableHeaderContainer">
        <div className="yourTicketsSectionRequesterContainer">
          <h3 className="noMargin yourTicketsSectionContentContainerHeader">
            Call Type
          </h3>
        </div>
        <div className="yourTicketsSectionRequesterContainer">
          <h3 className="noMargin yourTicketsSectionContentContainerHeader">
            User
          </h3>
        </div>
        <div className="yourTicketsSectionRequesterContainer">
          <h3 className="noMargin yourTicketsSectionContentContainerHeader">
            Customer
          </h3>
        </div>
        <div className="yourTicketsSectionUpdatedAtContainer">
          <h3 className="noMargin yourTicketsSectionContentContainerHeader">
            Created At
          </h3>
        </div>
      </div>
      {status === STATUS.loading && (
        <div className="loader">
          <div className="loader-overlay">
            <TailSpin
              width={60}
              height={60}
              color="#E0E0E0"
              ariaLabel="loading"
            />
          </div>
        </div>
      )}
      <div className="ticketList">
        {phoneCalls?.data?.map((call) => {
          const { attributes } = call;
          const attrs = attributes || call;
          const isInbound = attrs.direction === "inbound";
          const isUnanswered = attrs.type === "unanswered";
          const agentRelId = call.relationships?.support_agent?.data?.id;
          const agentIncluded = phoneCalls.included?.find(
            (i) => i.type === "support_agent" && i.id === agentRelId
          );
          const userName = agentIncluded?.attributes?.name || "—";
          const customerNumber = isInbound
            ? attrs.from_number || "—"
            : attrs.to_number || "—";

          return (
            <div
              className="ticketListItem"
              key={call.id}
              onClick={() => onCallClick(call)}
            >
              <div className="ticketListItemRequester">
                <div
                  style={{ display: "flex", alignItems: "center", gap: "6px" }}
                >
                  {isInbound ? <InboundArrow /> : <OutboundArrow />}
                  <div>
                    <span>{isInbound ? "Inbound" : "Outbound"}</span>
                    {isUnanswered && (
                      <div
                        style={{
                          fontSize: "11px",
                          color: "#9e9e9e",
                          marginTop: "1px",
                        }}
                      >
                        Unanswered
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <div className="ticketListItemRequester">{userName}</div>
              <div className="ticketListItemRequester">{customerNumber}</div>
              <div className="ticketListItemUpdatedAt">
                {formatDate(
                  attrs.telephony_supplier_created_at || "",
                  "yyyy-MM-dd H:mm"
                )}
              </div>
            </div>
          );
        })}
      </div>
      <div>
        <Pagination
          current={page}
          limit={10}
          onPageChanged={onPageChange}
          hasMore={phoneCalls?.meta?.has_more ?? null}
          total={phoneCalls?.meta?.pagination?.total ?? null}
        />
      </div>
      {selectedCall && (
        <PhoneCallModal call={selectedCall} onClose={onModalClose} />
      )}
    </div>
  );
};

export default SupplierPhoneCalls;
